const get = require('lodash/get')
const { getServiceForLocationId } = require('../../service/tools/territory-lookup')
const { safeDateParser } = require('../../shipment/tools/safe-date-parser')
const getPeriodFromProgram = require('../../report/tools/get-period-from-program')
const createReport = require('../../report/tools/create')
const { bulkUpsert } = require('../../report/data-access/pouchdb-adapter')
const { sanitiseBatchNo } = require('../../batch/tools')

function createInventoryAdjustment ({ mainApi, logger, batches }) {
  return async function inventoryAdjustment (params) {
    if (!params) {
      const err = new Error('inventoryAdjustment: payload is required')
      err.status = 400
      throw err
    }

    const singleAdjustment = Array.isArray(params.inventory_adjustment_data) ? params.inventory_adjustment_data[0] : null

    const warehouseCode = singleAdjustment && singleAdjustment.warehouse

    if (!singleAdjustment || !warehouseCode) {
      const err = new Error('inventoryAdjustment: warehouse and adjustment payload are required')
      err.status = 400
      throw err
    }

    if (!singleAdjustment.sku_code) {
      const err = new Error('inventoryAdjustment: sku_code is required')
      err.status = 400
      throw err
    }

    const adjustedQuantityValue = singleAdjustment.adjusted_quantity
    const adjustedQuantity = Number(adjustedQuantityValue)
    if (!Number.isFinite(adjustedQuantity)) {
      const err = new Error('inventoryAdjustment: adjusted_quantity is required')
      err.status = 400
      throw err
    }
    const updatedQuantityValue = Number(singleAdjustment.updated_quantity)

    const location = await mainApi.location.pgAdapter.getOneByCode(warehouseCode)
    const locationId = location.fsid

    const territory = getServiceForLocationId(locationId)
    const serviceId = typeof territory === 'string'
      ? territory
      : (territory && (territory.service || territory.id))

    if (!serviceId) {
      if (logger && logger.warn) {
        logger.warn('inventoryAdjustment: skipping adjustment due to missing service', {
          warehouse: warehouseCode,
          locationId,
          sku_code: singleAdjustment.sku_code
        })
      }
      return
    }

    const service = await mainApi.service.get(serviceId)

    let ledger
    try {
      ledger = await mainApi.stock.getLedgerBalanceAsReport({
        locationId,
        service,
        excludeProducts: 'configuration',
        calculateFields: true,
        api: mainApi,
        usePGLedger: true
      })
    } catch (err) {
      throw new Error(`inventoryAdjustment: unable to load ledger for ${locationId}: ${err.message || err}`)
    }

    if (!ledger || !ledger.stock) {
      throw new Error('inventoryAdjustment: ledger did not include stock data')
    }

    const productKey = `product:${singleAdjustment.sku_code}`
    const stockFromLedger = ledger.stock || {}
    const clonedStock = Object.keys(stockFromLedger).reduce((acc, key) => {
      const product = stockFromLedger[key] || {}
      acc[key] = {
        ...product,
        fields: { ...(product.fields || {}) }
      }
      if (product.batches) {
        acc[key].batches = { ...product.batches }
      }
      if (key !== productKey) {
        acc[key].autoFilled = true
      } else {
        delete acc[key].autoFilled
      }
      return acc
    }, {})

    if (!clonedStock[productKey]) {
      clonedStock[productKey] = { fields: {} }
    }

    // We get existing count and sum/deduct adjusted quantity
    const existingQuantity = (clonedStock[productKey].fields['field:standard-physical-count'] &&
      Number(clonedStock[productKey].fields['field:standard-physical-count'].amount)) || 0

    const newQuantity = existingQuantity + adjustedQuantity

    clonedStock[productKey].fields = clonedStock[productKey].fields || {}
    clonedStock[productKey].fields['field:standard-physical-count'] = { amount: newQuantity }
    clonedStock[productKey].fields['field:shelflife-balance'] = { amount: newQuantity }

    const batchNumberRaw = singleAdjustment.batch_number
    const batchNumber = batchNumberRaw ? sanitiseBatchNo(batchNumberRaw) : 'UNKNOWN'
    if (batchNumber) {
      const manufacturer = 'unknown'
      const batchKey = `${productKey}:manufacturer:${manufacturer}:batchNo:${batchNumber}`
      const currentBatch = get(clonedStock[productKey], ['batches', batchKey], {})
      const currentBatchFields = currentBatch.fields || {}
      clonedStock[productKey].batches = clonedStock[productKey].batches || {}
      clonedStock[productKey].batches[batchKey] = {
        ...currentBatch,
        fields: {
          ...currentBatchFields,
          'field:standard-physical-count': { amount: updatedQuantityValue }
        }
      }

      await batches.upsertBatchDetails({
        skuCode: singleAdjustment.sku_code,
        batchNo: batchNumber,
        manufacturer
      })
    }

    let stock = clonedStock

    const now = new Date().toISOString()
    const referenceDate = safeDateParser(singleAdjustment.adjusted_at || singleAdjustment.created_at) || now
    const period = getPeriodFromProgram(service.program, referenceDate, true)
    const username = singleAdjustment.adjusted_by

    const stockCountDoc = createReport({
      locationId,
      service,
      period,
      username,
      createdAt: now,
      submittedAt: now,
      stock,
      partialCount: true
    })

    stockCountDoc.documents = ledger.documents || {}
    stockCountDoc.partialCount = true

    await bulkUpsert(mainApi.state, [stockCountDoc])

    if (logger && logger.info) {
      logger.info('inventoryAdjustment: created partial count from Shipsy payload', {
        warehouse: warehouseCode,
        sku_code: singleAdjustment.sku_code,
        adjustedQuantity,
        reportId: stockCountDoc._id
      })
    }
  }
}

module.exports = createInventoryAdjustment
